
import React from 'react';
import { EnhancedFileUpload } from './EnhancedFileUpload';
import { FileBrowser } from './FileBrowser';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export const FileManagementTabs: React.FC = () => {
  const queryClient = useQueryClient();

  const handleFileSelection = async (bucket: string, filePath: string, fileName: string) => {
    try {
      console.log('[FILE-SELECTION] Starting progressive analysis for:', { bucket, filePath, fileName });

      // Create analysis record for existing file
      const { data: analysisRecord, error: dbError } = await supabase
        .from('sys_util_csv_analysis')
        .insert({
          file_name: fileName,
          file_path: filePath,
          content_type: 'text/csv',
          size: 0, // We don't have size info upfront for existing files
          analysis_status: 'pending'
        })
        .select()
        .single();

      if (dbError) {
        console.error('[FILE-SELECTION] Database error:', dbError);
        throw new Error(`Database error: ${dbError.message}`);
      }

      console.log('[FILE-SELECTION] Created analysis record:', analysisRecord);

      // Show progressive analysis toast
      const { dismiss: dismissProgressToast } = toast({
        title: "Progressive analysis starting",
        description: `Processing ${fileName} with progressive hybrid approach. Pass 1: Header extraction, Pass 2: Sample analysis for optimal performance.`,
      });

      // Trigger progressive analysis
      console.log('[FILE-SELECTION] Invoking progressive analyze-csv function with:', { 
        fileId: filePath,
        analysisId: analysisRecord.id,
        bucket: bucket
      });

      const startTime = Date.now();

      const { data: analysisResponse, error: analysisError } = await supabase.functions
        .invoke('analyze-csv', {
          body: { 
            fileId: filePath,
            analysisId: analysisRecord.id,
            bucket: bucket
          },
        });

      const processingTime = ((Date.now() - startTime) / 1000).toFixed(1);

      // Dismiss the progress toast
      dismissProgressToast();

      if (analysisError) {
        console.error('[FILE-SELECTION] Progressive analysis function error:', analysisError);
        
        // Get more details from the analysis record
        const { data: failedAnalysis } = await supabase
          .from('sys_util_csv_analysis')
          .select('analysis_result')
          .eq('id', analysisRecord.id)
          .single();

        // Safely extract error from analysis_result
        let errorDetails = analysisError.message;
        if (failedAnalysis?.analysis_result && typeof failedAnalysis.analysis_result === 'object') {
          const resultObj = failedAnalysis.analysis_result as Record<string, any>;
          errorDetails = resultObj.error || analysisError.message;
        }
        
        toast({
          title: "Progressive analysis failed",
          description: `Failed to analyze ${fileName}: ${errorDetails}`,
          variant: "destructive",
        });
        
        throw new Error(`Progressive analysis failed: ${errorDetails}`);
      }

      console.log('[FILE-SELECTION] Progressive analysis function response:', analysisResponse);

      // Check if the response indicates success
      if (analysisResponse && !analysisResponse.success) {
        console.error('[FILE-SELECTION] Progressive analysis function returned error:', analysisResponse);
        
        const errorMsg = analysisResponse.error || 'Unknown progressive analysis error';
        
        toast({
          title: "Progressive analysis failed",
          description: `Analysis failed: ${errorMsg}`,
          variant: "destructive",
        });
        
        throw new Error(`Progressive analysis failed: ${errorMsg}`);
      }

      // Check processing method used
      const processingInfo = analysisResponse?.analysis?.processingInfo;
      const isEmergencyMode = processingInfo?.emergencyMode;
      const isHeaderOnlyMode = processingInfo?.headerOnlyMode;
      const isProgressiveMode = processingInfo?.progressiveMode;
      const isUltraLightweight = processingInfo?.ultraLightweight;
      const isSampled = processingInfo?.isSampled;
      const sampleSize = processingInfo?.sampleSize;
      
      // Show appropriate success message based on processing mode
      let description = `${fileName} analyzed in ${processingTime}s`;
      
      if (isEmergencyMode && isHeaderOnlyMode) {
        description += '. Progressive header-only mode: Extracted meaningful column names. Schema created with TEXT columns ready for data import and optional type optimization.';
      } else if (isEmergencyMode) {
        description += '. Emergency mode: File too large for detailed analysis. Consider splitting file for detailed processing.';
      } else if (isProgressiveMode) {
        description += `. Progressive hybrid analysis completed${sampleSize ? ` (${sampleSize} sample rows analyzed)` : ''}. Headers extracted, schema ready with TEXT columns. You can now import data and optimize column types.`;
      } else if (isUltraLightweight) {
        description += `. Ultra-lightweight processing used${sampleSize ? ` (${sampleSize} sample rows analyzed)` : ''}. You can now generate schema and import data.`;
      } else if (isSampled) {
        description += `. Sampled analysis completed${sampleSize ? ` (${sampleSize} sample rows analyzed)` : ''}. You can now generate schema and import data.`;
      } else {
        description += '. Complete analysis finished. You can now generate schema and import data.';
      }
      
      toast({
        title: "Progressive analysis completed successfully",
        description,
        className: isEmergencyMode && !isHeaderOnlyMode ? "bg-[#FEF3C7] border-yellow-200 text-yellow-800" : "bg-[#F2FCE2] border-green-200 text-green-800",
      });

      // Invalidate and refetch the csv analyses query
      await queryClient.invalidateQueries({ queryKey: ['csvAnalyses'] });

    } catch (error: any) {
      console.error('[FILE-SELECTION] Progressive file selection error:', error);
      
      toast({
        title: "Progressive analysis failed",
        description: error.message || "Failed to analyze the selected file with progressive approach. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>File Management</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <EnhancedFileUpload />
          <FileBrowser onFileSelect={handleFileSelection} />
        </div>
      </CardContent>
    </Card>
  );
};
